import type { KpiSource, KpiSourceId } from '../types';
import { ageGatingSource } from './age-gating';
import { ageBracketsSource } from './age-brackets';
import { profileCreationConversionSource } from './profile-creation-conversion';
import { couponActivationSource } from './coupon-activation';
import { downloadsSource } from './downloads';
import { appDownloadsTotalSource } from './app-downloads-total';
import { carwashEngagementSource } from './carwash-engagement';
import { totalUsersSource } from './total-users';
import { featureActiveUsersSource } from './feature-active-users';
import { paymentMethodsAddedSource } from './payment-methods-added';
import { newUsersSource } from './new-users';
import { newUsersRppSource } from './new-users-rpp';
import { engagementSource } from './engagement';
import { engagementRppSource } from './engagement-rpp';
import { optInsSource } from './opt-ins';
import { optInsRppSource } from './opt-ins-rpp';
import { gameEngagementRppSource } from './game-engagement-rpp';
import { technicalHealthSource } from './technical-health';
import { engagementDriversRppSource } from './engagement-drivers-rpp';
import { six12PurchasesSource } from './six12-purchases';
import { couponIssuedSource } from './coupon-issued';
import { couponIssuedRppSource } from './coupon-issued-rpp';
import { couponRedeemedSource } from './coupon-redeemed';
import { couponRedeemedRppSource } from './coupon-redeemed-rpp';
import { couponRedeemedAgeRestrictedSource } from './coupon-redeemed-age-restricted';
import { couponTopRedeemedSource } from './coupon-top-redeemed';
import { onboardingStateSource } from './onboarding-state';
import { placeholderSource } from './placeholder';
import { churnSource } from './churn';
import { loginActivitySource } from './login-activity';
import { loginActivityRppSource } from './login-activity-rpp';
import { loginRetentionSource } from './login-retention';
import { loginRetentionRppSource } from './login-retention-rpp';
import { campaignMembersSource } from './campaign-members';
import { campaignPurchasesSource } from './campaign-purchases';
import { campaignCouponsIssuedSource } from './campaign-coupons-issued';
import { campaignEnrollmentFunnelSource } from './campaign-enrollment-funnel';
import { campaignVisitsSource } from './campaign-visits';
import { campaignVisitsLedgerSource } from './campaign-visits-ledger';
import { campaignMilestonesSource } from './campaign-milestones';
import { campaignContactabilitySource } from './campaign-contactability';
import { campaignCouponsMemberSource } from './campaign-coupons-member';
import { campaignSegmentMemberSource } from './campaign-segment-member';
import { campaignNhlCoinsCollectionSource } from './campaign-nhl-coins-collection';
import { players31docSource } from './players-31doc';
import { doorOpenings31docSource } from './door-openings-31doc';
import { coupons31docSource } from './coupons-31doc';
import { purchases31docSource } from './purchases-31doc';
import { engagementDrivers31docSource } from './engagement-drivers-31doc';
import { campaignNhlCoinsPopularitySource } from './campaign-nhl-coins-popularity';
import { inactivityReturnSource } from './inactivity-return';

/**
 * Source registry keyed by `KpiSourceId`. Adding a real source is: implement
 * its `KpiSource` and register it here. Nothing in the bridge, forms, or page
 * changes.
 *
 * `satisfies`, not `:`. The annotation still enforces that every `KpiSourceId`
 * has an entry and that each entry is a `KpiSource`, but it no longer WIDENS
 * each value to the erased `KpiSource`: the per-key type keeps its own metric
 * union, which is what `card-spec.ts` reads back to give each card its legal
 * metric domain. Switching this back to `:` compiles fine and silently reopens
 * the mispaired-card hole, so leave it.
 */
export const SOURCES = {
    'age-gating': ageGatingSource,
    'age-brackets': ageBracketsSource,
    'profile-creation-conversion': profileCreationConversionSource,
    'coupon-activation': couponActivationSource,
    'app-downloads': downloadsSource,
    'app-downloads-total': appDownloadsTotalSource,
    'carwash-engagement': carwashEngagementSource,
    'total-users': totalUsersSource,
    'feature-active-users': featureActiveUsersSource,
    'payment-methods-added': paymentMethodsAddedSource,
    'new-users': newUsersSource,
    'new-users-rpp': newUsersRppSource,
    engagement: engagementSource,
    'engagement-rpp': engagementRppSource,
    'opt-ins': optInsSource,
    'opt-ins-rpp': optInsRppSource,
    'game-engagement-rpp': gameEngagementRppSource,
    'technical-health': technicalHealthSource,
    'engagement-drivers-rpp': engagementDriversRppSource,
    'six12-purchases': six12PurchasesSource,
    'coupon-issued': couponIssuedSource,
    'coupon-issued-rpp': couponIssuedRppSource,
    'coupon-redeemed': couponRedeemedSource,
    'coupon-redeemed-rpp': couponRedeemedRppSource,
    'coupon-redeemed-age-restricted': couponRedeemedAgeRestrictedSource,
    'coupon-top-redeemed': couponTopRedeemedSource,
    'onboarding-state': onboardingStateSource,
    placeholder: placeholderSource,
    churn: churnSource,
    'login-activity': loginActivitySource,
    'login-activity-rpp': loginActivityRppSource,
    'login-retention': loginRetentionSource,
    'login-retention-rpp': loginRetentionRppSource,
    'campaign-members': campaignMembersSource,
    'campaign-purchases': campaignPurchasesSource,
    'campaign-coupons-issued': campaignCouponsIssuedSource,
    'campaign-enrollment-funnel': campaignEnrollmentFunnelSource,
    'campaign-visits': campaignVisitsSource,
    'campaign-visits-ledger': campaignVisitsLedgerSource,
    'campaign-milestones': campaignMilestonesSource,
    'campaign-contactability': campaignContactabilitySource,
    'campaign-coupons-member': campaignCouponsMemberSource,
    'campaign-segment-member': campaignSegmentMemberSource,
    'campaign-nhl-coins-collection': campaignNhlCoinsCollectionSource,
    'campaign-nhl-coins-popularity': campaignNhlCoinsPopularitySource,
    'inactivity-return': inactivityReturnSource,
    'players-31doc': players31docSource,
    'door-openings-31doc': doorOpenings31docSource,
    'coupons-31doc': coupons31docSource,
    'purchases-31doc': purchases31docSource,
    'engagement-drivers-31doc': engagementDrivers31docSource,
} satisfies Record<KpiSourceId, KpiSource>;
